import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, Mail, Phone, Briefcase, ShieldCheck } from "lucide-react";

const STATUS_COLORS = {
  todo: "bg-amber-50 text-amber-800 border-amber-300",
  in_progress: "bg-blue-50 text-blue-800 border-blue-300",
  done: "bg-green-50 text-green-800 border-green-300",
};

export default function UserDetail() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [attendance, setAttendance] = useState([]);
  const [leads, setLeads] = useState([]);

  useEffect(() => {
    (async () => {
      const [u, t, a, l] = await Promise.all([
        api.get("/users").then((r) => r.data.find((x) => x.id === id)),
        api.get("/tasks").then((r) => r.data.filter((x) => x.assignee_id === id)),
        api.get("/attendance/all").then((r) => r.data.filter((x) => x.user_id === id)).catch(() => []),
        api.get("/leads").then((r) => r.data.filter((x) => x.assignee_id === id || x.created_by === id)),
      ]);
      setUser(u);
      setTasks(t);
      setAttendance(a);
      setLeads(l);
    })();
  }, [id]);

  if (!user) return <div className="p-10 label-uppercase">Loading…</div>;

  const openTasks = tasks.filter((t) => t.status !== "done").length;
  const doneTasks = tasks.filter((t) => t.status === "done").length;
  const presentDays = attendance.filter((a) => a.check_in).length;

  return (
    <div className="min-h-screen" data-testid="user-detail-page">
      <header className="border-b border-zinc-200 page-px py-6">
        <Link to="/users" className="text-xs uppercase tracking-wider text-zinc-500 hover:text-[#002FA7] flex items-center gap-1 mb-3">
          <ArrowLeft size={12} /> Back to employees
        </Link>
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 bg-zinc-900 text-white flex items-center justify-center text-xl font-bold">
            {user.name?.[0]}
          </div>
          <div>
            <div className="label-uppercase mb-1">Employee</div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tighter" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>{user.name}</h1>
            <div className="flex items-center gap-3 text-xs uppercase tracking-wider text-zinc-500 mt-1">
              {user.role === "admin" && <span className="flex items-center gap-1"><ShieldCheck size={11}/> admin</span>}
              {user.role === "employee" && <span>employee</span>}
              {user.department && <span>· {user.department}</span>}
            </div>
          </div>
        </div>
      </header>

      <section className="grid grid-cols-2 lg:grid-cols-4 border-b border-zinc-200">
        <Metric label="Open Tasks" value={openTasks} />
        <Metric label="Completed" value={doneTasks} />
        <Metric label="Days Present" value={presentDays} />
        <Metric label="Leads Owned" value={leads.length} />
      </section>

      <section className="grid lg:grid-cols-12 border-b border-zinc-200">
        <div className="lg:col-span-4 lg:border-r border-b lg:border-b-0 border-zinc-200 p-6 sm:p-8 space-y-4">
          <div className="label-uppercase">Contact</div>
          <Row icon={Mail} label="Email" value={user.email} />
          <Row icon={Phone} label="Phone" value={user.phone || "—"} />
          <Row icon={Briefcase} label="Department" value={user.department || "—"} />
        </div>

        <div className="lg:col-span-8 p-6 sm:p-8">
          <h2 className="text-lg font-medium tracking-tight mb-4">Assigned Tasks</h2>
          <div className="border border-zinc-200">
            <div className="grid grid-cols-12 label-uppercase border-b border-zinc-200 px-4 py-2 bg-zinc-50">
              <div className="col-span-6">Title</div>
              <div className="col-span-2">Priority</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-2 text-right">Due</div>
            </div>
            {tasks.length === 0 && <div className="px-4 py-8 text-sm text-zinc-500">No tasks assigned.</div>}
            {tasks.map((t) => (
              <Link to={`/tasks/${t.id}`} key={t.id} className="grid grid-cols-12 px-4 py-3 border-b border-zinc-200 text-sm hover:bg-zinc-50">
                <div className="col-span-6 truncate font-medium">{t.title}</div>
                <div className="col-span-2 capitalize">{t.priority === "low" ? "normal" : t.priority}</div>
                <div className="col-span-2">
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${STATUS_COLORS[t.status]}`}>
                    {t.status.replace("_"," ")}
                  </span>
                </div>
                <div className="col-span-2 text-right font-mono text-xs text-zinc-500">{t.due_date || "—"}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="p-6 sm:p-8">
        <h2 className="text-lg font-medium tracking-tight mb-4">Recent Attendance</h2>
        <div className="border border-zinc-200">
          <div className="grid grid-cols-12 label-uppercase border-b border-zinc-200 px-4 py-2 bg-zinc-50">
            <div className="col-span-4">Date</div>
            <div className="col-span-3">Check-in</div>
            <div className="col-span-3">Check-out</div>
            <div className="col-span-2 text-right">Hours</div>
          </div>
          {attendance.length === 0 && <div className="px-4 py-8 text-sm text-zinc-500">No attendance records.</div>}
          {attendance.slice(0, 30).map((r) => {
            const hrs = r.check_in && r.check_out
              ? ((new Date(r.check_out) - new Date(r.check_in)) / 3600000).toFixed(2)
              : "—";
            return (
              <div key={r.id} className="grid grid-cols-12 px-4 py-2 border-b border-zinc-200 text-sm">
                <div className="col-span-4 font-mono">{r.date}</div>
                <div className="col-span-3 font-mono text-xs">{r.check_in ? new Date(r.check_in).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</div>
                <div className="col-span-3 font-mono text-xs">{r.check_out ? new Date(r.check_out).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}</div>
                <div className="col-span-2 text-right font-mono">{hrs}</div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="p-8 border-l border-zinc-200 first:border-l-0">
      <div className="label-uppercase mb-3">{label}</div>
      <div className="text-4xl font-bold tracking-tighter font-mono">{value}</div>
    </div>
  );
}
function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={14} className="text-zinc-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <div className="label-uppercase mb-0.5">{label}</div>
        <div className="text-sm break-all">{value}</div>
      </div>
    </div>
  );
}
